// Checklist de pendências do pedido em 'Em inclusão' (FEAT-015): o que falta
// para liberar o "Enviar para Análise". Fonte única — usada pelo botão (gate)
// e onde mais precisar exibir as pendências.

// Domínio fixo dos tamanhos de impressão (mesma ordem do rol usado nos
// agrupamentos) e das classificações (FEAT-013) — fonte única para as galerias
// de capturadas e editadas.
export const PHOTO_SIZES = [
  '10x15',
  '13x18',
  '15x21',
  '20x25',
  '20x30',
  '30x30',
  '30x40',
  '60x90',
  '90x120',
];

export const PHOTO_CLASSIFICATIONS = ['Fotolivro', 'Foto avulsa'];

// Quantidades esperadas por tamanho, derivadas dos PRODUTOS do pedido.
// Fotolivro SEM tamanho = ADICIONAL DE ÁLBUM (ex.: 'Foto Adicional Fotolivro'
// soma photos_quantity fotos ao álbum por unidade; o tamanho acompanha o álbum).
export const expectedPhotosByProducts = (order) => {
  const album = {};
  const avulsa = {};
  let albumExtra = 0;

  (order.products || []).forEach((p) => {
    const qty = Number(p.quantity || 1);

    if (p.type === 'Fotolivro' && p.photos_quantity) {
      if (p.photo_size) {
        album[p.photo_size] =
          (album[p.photo_size] || 0) + p.photos_quantity * qty;
      } else {
        albumExtra += p.photos_quantity * qty;
      }
    }

    if (
      (p.type === 'Revelação digital' || p.type === 'Impressão plotter') &&
      p.photo_size
    ) {
      avulsa[p.photo_size] = (avulsa[p.photo_size] || 0) + qty;
    }
  });

  // Adicionais têm o tamanho do álbum: com UM único tamanho de álbum no pedido,
  // o contador por tamanho já os incorpora; senão entram só no total.
  const albumSizes = Object.keys(album);
  let albumTotal = Object.values(album).reduce((s, v) => s + v, 0);

  if (albumExtra > 0) {
    if (albumSizes.length === 1) {
      album[albumSizes[0]] += albumExtra;
    }
    albumTotal += albumExtra;
  }

  return { album, avulsa, albumTotal };
};

// Pendências de atributos/contagem de um conjunto de fotos (capturadas ou
// editadas) contra os PRODUTOS do pedido: todas com tamanho e classificação;
// álbum X de N; avulsas por tamanho conforme a quantidade vendida.
const photoCountPendencies = (order, photos, label) => {
  const pendencies = [];

  const withoutSize = photos.filter((p) => !p.photo_size).length;
  if (withoutSize > 0) {
    pendencies.push(`${withoutSize} foto(s) ${label} sem tamanho`);
  }

  const withoutClassification = photos.filter(
    (p) => !p.photo_classification
  ).length;
  if (withoutClassification > 0) {
    pendencies.push(
      `${withoutClassification} foto(s) sem classificação (Fotolivro/Foto avulsa)`
    );
  }

  const { avulsa, albumTotal } = expectedPhotosByProducts(order);

  // Quantidades são MÍNIMOS (ajuste 06/07): fotolivro de 20 fotos precisa de
  // PELO MENOS 20 — pode ter mais. Vale também para as avulsas por tamanho.
  if (albumTotal > 0) {
    const albumCount = photos.filter(
      (p) => p.photo_classification === 'Fotolivro'
    ).length;

    if (albumCount < albumTotal) {
      pendencies.push(
        `Fotos do álbum: ${albumCount} de ${albumTotal} (mínimo)`
      );
    }
  }

  Object.entries(avulsa).forEach(([size, expected]) => {
    const count = photos.filter(
      (p) => p.photo_classification === 'Foto avulsa' && p.photo_size === size
    ).length;

    if (count < expected) {
      pendencies.push(
        `Fotos avulsas ${size}: ${count} de ${expected} (mínimo)`
      );
    }
  });

  return pendencies;
};

// Lista de pendências (strings) para enviar o pedido à análise financeira.
// Vazia = requisitos atendidos.
export const inclusionPendencies = (order) => {
  const pendencies = [];

  if (!(order.spcfiles && order.spcfiles.length > 0)) {
    pendencies.push('Anexar o arquivo SPC');
  }

  if (!(order.products && order.products.length > 0)) {
    pendencies.push('Incluir os produtos do pedido');
    return pendencies; // sem produtos não há como validar fotos
  }

  const captured = (order.photos || []).filter(
    (p) => p.photo_type === 'Capturada'
  );

  // Caminho legado: sem capturadas, o Nº SIGI substitui as fotos.
  if (captured.length === 0) {
    if (!order.sig_number) {
      pendencies.push('Anexar as fotos capturadas ou informar o Nº SIGI');
    }
    return pendencies;
  }

  return [
    ...pendencies,
    ...photoCountPendencies(order, captured, 'capturada(s)'),
  ];
};

// Pendências para CONCLUIR A EDIÇÃO (FEAT-018): mesma régua da inclusão,
// aplicada às fotos EDITADAS — ≥1 editada, capa definida, todas com tamanho e
// classificação, contadores fechados. Espelhada no backend
// (OrdersRepository.get_edition_pendencies). Vazia = requisitos atendidos.
export const editionPendencies = (order) => {
  if (!(order.products && order.products.length > 0)) {
    return ['Incluir os produtos do pedido'];
  }

  const edited = (order.photos || []).filter((p) => p.photo_type === 'Editada');

  if (edited.length === 0) {
    return ['Anexar as fotos editadas'];
  }

  const pendencies = [];

  if (!edited.some((p) => p.is_cover_photo)) {
    pendencies.push('Selecionar a foto principal (capa)');
  }

  // Foto reprovada pela inspeção não pode voltar como está (05/07): o editor
  // remove a reprovada e sobe a substituta (que nasce 'Aguarda inspeção').
  const reproved = edited.filter((p) => p.photo_status === 'Reprovada').length;
  if (reproved > 0) {
    pendencies.push(
      `${reproved} foto(s) reprovada(s) — substitua antes de finalizar`
    );
  }

  return [...pendencies, ...photoCountPendencies(order, edited, 'editada(s)')];
};

// Pendências para CONCLUIR A EDIÇÃO DE LÂMINAS (05/07): ≥1 lâmina e nenhuma
// reprovada pela inspeção. Espelhada no backend
// (OrdersRepository.get_spread_edition_pendencies). Vazia = ok.
export const spreadEditionPendencies = (order) => {
  const spreads = (order.photos || []).filter((p) => p.photo_type === 'Lâmina');

  if (spreads.length === 0) {
    return ['Anexar as lâminas do fotolivro'];
  }

  const pendencies = [];

  const reproved = spreads.filter((p) => p.photo_status === 'Reprovada').length;
  if (reproved > 0) {
    pendencies.push(
      `${reproved} lâmina(s) reprovada(s) — substitua antes de finalizar`
    );
  }

  return pendencies;
};
